import React, { useCallback, useState } from 'react';
import { type LayoutChangeEvent } from 'react-native';
import {
    BodyLargeRegular,
    BodySmallEmphasized,
    Button,
    ButtonVariant,
    FontFamilyName,
    H6Emphasized,
    Icon,
    Image,
    Info,
    LabelRegular,
    Spinner,
    SpinnerSize,
    TooltipTrigger,
    XStack,
    YStack,
} from 'smoothie';

import GuidesImage from '../../assets/images/guides.png';
import LeavesImage from '../../assets/images/leaves.svg';
import StarsImage from '../../assets/images/stars.svg';
import { useConversationalMMContext } from '../context/ConversationalMMContextBase';
import {
    AdvisoryDisclosure,
    Header,
    LinearGradientBackgroundCard,
} from './shared';

const InvestingCtaSlide = () => {
    const { terminalAction } = useConversationalMMContext();
    const [containerWidth, setContainerWidth] = useState(0);

    const onLayout = useCallback((e: LayoutChangeEvent) => {
        setContainerWidth(e.nativeEvent.layout.width);
    }, []);

    return (
        <YStack
            width="100%"
            justifyContent="center"
            alignItems="center"
            gap={24}
        >
            <LinearGradientBackgroundCard>
                <YStack
                    gap={32}
                    width="100%"
                    justifyContent="center"
                    alignItems="center"
                    onLayout={onLayout}
                >
                    <Header title="Put it into action" />
                    <BodyLargeRegular
                        style={{
                            fontFamily: FontFamilyName.SourceSerifRegular,
                        }}
                    >
                        This is your starting point, built from everything
                        you&apos;ve shared so far.
                    </BodyLargeRegular>
                    <BodyLargeRegular
                        style={{
                            fontFamily: FontFamilyName.SourceSerifRegular,
                        }}
                    >
                        Having a personal financial Guide advising on and
                        supporting your decisions, combined with our streamlined
                        process for setting up and scaling your investments
                        means that you get a portfolio designed to meet your
                        goals without confusion or the negative impact caused by
                        management fees.
                    </BodyLargeRegular>
                    <Image
                        source={GuidesImage}
                        style={{
                            width: containerWidth + 32,
                            height: containerWidth / 2,
                            marginBottom: -48,
                        }}
                    />
                    <BodyLargeRegular
                        style={{
                            fontFamily: FontFamilyName.SourceSerifRegular,
                        }}
                    >
                        In your first session, your Fruitful Guide — a CFP®
                        Professional — will work with you to recommend the
                        account type and asset allocation that’s right for you
                        based on several factors, including your goals, risk
                        tolerance, and future plans.
                    </BodyLargeRegular>
                    {terminalAction.mode === 'signup' && (
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            text="Continue"
                            icon={
                                terminalAction.isLoading === true ? (
                                    <Spinner
                                        size={SpinnerSize.SMALL}
                                        width="auto"
                                    />
                                ) : undefined
                            }
                            disabled={terminalAction.isLoading}
                            onPress={terminalAction.onPress}
                        />
                    )}
                    {terminalAction.mode === 'exit' && (
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            text={terminalAction.label ?? 'Back to homepage'}
                            onPress={terminalAction.onPress}
                        />
                    )}
                    {terminalAction.mode === 'signup' && (
                        <XStack
                            alignSelf="center"
                            alignItems="center"
                            justifyContent="center"
                            gap={16}
                        >
                            <LeavesImage width={28} height={69} />
                            <YStack
                                flex={1}
                                justifyContent="center"
                                alignItems="center"
                                gap={8}
                            >
                                <YStack
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <H6Emphasized>Excellent</H6Emphasized>
                                    <LabelRegular>Rated 4.8/5</LabelRegular>
                                </YStack>
                                <StarsImage width={110} height={18} />
                                <XStack alignItems="center" gap={4}>
                                    <BodySmallEmphasized>
                                        Trustpilot
                                    </BodySmallEmphasized>
                                    <TooltipTrigger tooltipContent="Trustpilot rating based on reviews as of July 8th, 2026. Reviews may not reflect all members experience and are not indicative of future performance. All reviews on Trustpilot can be found at Trustpilot.com/review/fruitful.com.">
                                        <XStack cursor="pointer">
                                            <Icon icon={Info} size={14} />
                                        </XStack>
                                    </TooltipTrigger>
                                </XStack>
                            </YStack>
                            <LeavesImage
                                width={28}
                                height={69}
                                style={{ transform: [{ scaleX: -1 }] }}
                            />
                        </XStack>
                    )}
                </YStack>
            </LinearGradientBackgroundCard>
            <YStack width="100%" gap={12}>
                <AdvisoryDisclosure />
                <LabelRegular color="$gray1000">
                    Certified Financial Planner Board of Standards, Inc. (CFP
                    Board) owns the CFP® certification mark, the CERTIFIED
                    FINANCIAL PLANNER® certification mark, and the CFP®
                    certification mark (with plaque design) logo in the United
                    States, which it authorizes use of by individuals who
                    successfully complete CFP Board&apos;s initial and ongoing
                    certification requirements.
                </LabelRegular>
            </YStack>
        </YStack>
    );
};

export default InvestingCtaSlide;
